const key = 'realrain_ui_preferences';
const eventName = 'realrain:ui-preferences';
const darkScheme = '(prefers-color-scheme: dark)';
const syncPath = '/api/v1/me/preferences/sync';
const migratedKey = 'realrain:theme-migrated';
const refreshInterval = 60_000;
const writeDelay = 300;

const isTheme = value => value === 'light' || value === 'dark';
const valid = value => ({
  ...(isTheme(value?.theme) ? { theme: value.theme } : {}),
  ...(Number.isFinite(value?.themeAt) && value.themeAt >= 0 ? { themeAt: Math.floor(value.themeAt) } : {}),
  ...(typeof value?.themeAccount === 'string' && /^[\w-]{8,64}$/.test(value.themeAccount) ? { themeAccount: value.themeAccount } : {}),
  ...(['zh', 'en', 'ja'].includes(value?.locale) ? { locale: value.locale } : {}),
});

/*
 * Non-sensitive display preferences shared by every RealRain surface across
 * product ports and hosts, kept as URI-encoded JSON in one cookie:
 *   theme         'light' | 'dark'; without it the system appearance applies
 *   themeAt       when the theme was chosen, ms since the epoch
 *   themeAccount  opaque tag of the account Account last aligned the theme with
 *   locale        'zh' | 'en' | 'ja'
 * The theme follows the signed-in account: Account's
 * POST /api/v1/me/preferences/sync keeps whichever of this cookie and the
 * account holds the more recent choice, and answers with the cookie updated.
 */
export function readUiPreferences() {
  try {
    const value = document.cookie.split('; ').find(row => row.startsWith(key + '='));
    return valid(value ? JSON.parse(decodeURIComponent(value.slice(key.length + 1))) : {});
  } catch { return {}; }
}

function persist(value) {
  try {
    const domain = /(^|\.)realrain\.art$/.test(location.hostname) ? '; Domain=realrain.art' : '';
    document.cookie = `${key}=${encodeURIComponent(JSON.stringify(value))}; Path=/; Max-Age=31536000; SameSite=Lax${location.protocol === 'https:' ? '; Secure' : ''}${domain}`;
  } catch { /* The current view still updates if persistence is unavailable. */ }
}

function announce() {
  if (typeof window !== 'undefined') window.dispatchEvent(new Event(eventName));
}

/** Re-reads the shared cookie, e.g. after the workspace around this frame changed it. */
export function refreshUiPreferences() {
  announce();
}

export function writeUiPreferences(patch) {
  const value = readUiPreferences();
  if (['zh', 'en', 'ja'].includes(patch?.locale)) value.locale = patch.locale;
  const chosen = isTheme(patch?.theme);
  if (chosen) {
    value.theme = patch.theme;
    // Later than anything this browser has seen, even when its clock is behind the account's.
    value.themeAt = Math.max(Date.now(), (value.themeAt ?? 0) + 1);
    // Account tags the choice with whoever is signed in when it arrives.
    delete value.themeAccount;
  }
  persist(value);
  announce();
  if (chosen) scheduleAccountSync();
}

export function subscribeUiPreferences(listener) {
  const read = () => listener(readUiPreferences());
  window.addEventListener(eventName, read);
  window.addEventListener('focus', read);
  window.addEventListener('pageshow', read);
  startAccountSync();
  read();
  return () => {
    window.removeEventListener(eventName, read);
    window.removeEventListener('focus', read);
    window.removeEventListener('pageshow', read);
  };
}

function darkSchemeQuery() {
  try { return typeof window !== 'undefined' && typeof window.matchMedia === 'function' ? window.matchMedia(darkScheme) : null; }
  catch { return null; }
}

/** The system appearance: dark when the device asks for it, otherwise light. */
export function systemTheme() {
  return darkSchemeQuery()?.matches ? 'dark' : 'light';
}

/** The chosen theme (the account's, once aligned), otherwise the system appearance. */
export function resolveTheme(preferences = readUiPreferences()) {
  return isTheme(preferences?.theme) ? preferences.theme : systemTheme();
}

export function subscribeSystemTheme(listener) {
  const query = darkSchemeQuery();
  if (!query) return () => {};
  const change = () => listener(query.matches ? 'dark' : 'light');
  if (typeof query.addEventListener === 'function') {
    query.addEventListener('change', change);
    return () => query.removeEventListener('change', change);
  }
  query.addListener?.(change);
  return () => query.removeListener?.(change);
}

/**
 * Calls `listener` with the theme to show now and whenever it changes: a new
 * choice here or in another product, the account's choice arriving, or a
 * system change while nothing is chosen.
 */
export function subscribeTheme(listener) {
  let current;
  const update = () => {
    const theme = resolveTheme();
    if (theme !== current) { current = theme; listener(theme); }
  };
  const stopSystem = subscribeSystemTheme(update);
  const stopPreferences = subscribeUiPreferences(update);
  return () => { stopPreferences(); stopSystem(); };
}

// Themes each product saved before the theme followed the account. Hub saved
// only choices people made. Platform Admin and Fair also saved their light
// default, so only a dark value there was chosen. Studio saved whatever it
// showed, including the system appearance of a first visit, so its values are
// not choices and stay behind.
const legacyThemes = [
  ['rainhub-theme', value => value],
  ['realrain:admin-theme', value => value === 'dark' ? value : null],
  ['rainfair-ui-fallback-v2', value => { try { return JSON.parse(value)?.theme === 'dark' ? 'dark' : null; } catch { return null; } }],
];

/**
 * Moves a theme this origin saved before the theme followed the account into
 * the shared preference, once. It counts as older than any account choice
 * (themeAt 1): the account's own choice still wins, and the old one only
 * fills an account that never chose.
 */
export function migrateLegacyTheme() {
  let storage;
  try {
    storage = window.localStorage;
    if (!storage || storage.getItem(migratedKey)) return false;
  } catch { return false; }
  let moved = false;
  try {
    if (!readUiPreferences().theme) {
      for (const [name, chosen] of legacyThemes) {
        const theme = chosen(storage.getItem(name));
        if (!isTheme(theme)) continue;
        const { themeAccount: _account, ...value } = readUiPreferences();
        persist({ ...value, theme, themeAt: 1 });
        moved = true;
        break;
      }
    }
    storage.setItem(migratedKey, '1');
  } catch { /* Without storage there is nothing to move. */ }
  if (moved) announce();
  return moved;
}

let accountSync = true;
let syncStarted = false;
let originTask = null;
let originMissedAt = -Infinity;
let running = null;
let queued = null;
let lastSyncAt = -Infinity;
let timer = null;

// Simulated DOMs in unit tests never reach the network on their own.
const automatic = () => accountSync && typeof window !== 'undefined' && typeof fetch === 'function'
  && !/\b(?:jsdom|HappyDOM)\//i.test(globalThis.navigator?.userAgent ?? '');

/** `accountSync: false` keeps this page from contacting Account on its own (a strict connect-src). */
export function configureUiPreferences(options = {}) {
  if (typeof options.accountSync === 'boolean') accountSync = options.accountSync;
}

function startAccountSync() {
  if (syncStarted || !automatic()) return;
  syncStarted = true;
  void syncAccountPreferences();
  const refresh = () => { if (automatic() && Date.now() - lastSyncAt >= refreshInterval) void syncAccountPreferences(); };
  window.addEventListener('focus', refresh);
  window.addEventListener('pageshow', event => { if (event.persisted) refresh(); });
  // keepalive lets a choice made just before leaving still reach Account.
  window.addEventListener('pagehide', () => {
    if (!timer) return;
    clearTimeout(timer);
    timer = null;
    void syncAccountPreferences();
  });
}

function scheduleAccountSync() {
  if (!automatic()) return;
  clearTimeout(timer);
  timer = setTimeout(() => { timer = null; void syncAccountPreferences(); }, writeDelay);
}

// The products publish where Account is, like account-identity.js reads it.
async function lookUpAccountOrigin() {
  const paths = [...(location.pathname.startsWith('/rainfair/') ? ['/rainfair/api/account/identity-config'] : []),
    '/api/account/identity-config', '/api/pcas/auth/account/identity-config'];
  for (const path of paths) {
    try {
      const timeout = typeof AbortSignal !== 'undefined' && typeof AbortSignal.timeout === 'function' ? { signal: AbortSignal.timeout(5000) } : {};
      const response = await fetch(path, { credentials: 'same-origin', ...timeout });
      if (!response.ok) continue;
      const value = await response.json();
      if (typeof value?.accountOrigin === 'string' && /^https?:\/\//.test(value.accountOrigin)) return new URL(value.accountOrigin).origin;
    } catch { /* Try the next product route. */ }
  }
  return null;
}

function accountOrigin() {
  if (originTask) return originTask;
  if (Date.now() - originMissedAt < refreshInterval) return Promise.resolve(null);
  originTask = lookUpAccountOrigin().then(origin => {
    if (!origin) { originTask = null; originMissedAt = Date.now(); }
    return origin;
  });
  return originTask;
}

function send() {
  lastSyncAt = Date.now();
  const task = (async () => {
    try {
      const origin = await accountOrigin();
      if (!origin) return false;
      // A simple POST without a body or custom headers: Account reads its own
      // session and this cookie, answers 204 with at most a new value for this
      // cookie, and the response stays opaque to this page.
      await fetch(new URL(syncPath, origin).href, { method: 'POST', mode: 'no-cors', credentials: 'include', keepalive: true, cache: 'no-store' });
      return true;
    } catch {
      return false;
    } finally {
      // Show whatever theme the account sent back.
      announce();
    }
  })();
  running = task;
  task.finally(() => { if (running === task) running = null; });
  return task;
}

/**
 * Aligns this browser's shared theme with the signed-in account. Resolves to
 * whether Account was reached; a request asked for while one is running
 * follows it, so a choice made meanwhile is not lost.
 */
export function syncAccountPreferences() {
  if (typeof window === 'undefined' || typeof fetch !== 'function') return Promise.resolve(false);
  if (!running) return send();
  if (!queued) queued = running.then(() => { queued = null; return send(); });
  return queued;
}

if (typeof window !== 'undefined') migrateLegacyTheme();
